declare global {
  interface Window {
    dataLayer?: Object[] // eslint-disable-line @typescript-eslint/no-wrapper-object-types
  }
}

// ─────────────────────────────────────────────────────────────────────────
// GTM shield
//
// Three problems this file defends against:
//
// 1. GTM tags can hand us payloads containing DOM nodes, React fiber refs,
//    functions, or circular refs. GTM's internal serialization then throws
//    and the error can bubble into React's transition path, producing the
//    "This page couldn't load" screen mid-navigation.
//
// 2. The GTM container ships a "Delayed Event Script" custom-HTML tag that
//    wraps `dataLayer.push` with the equivalent of:
//
//      var originalPush = dataLayer.push;
//      dataLayer.push = function() { ...; return originalPush.apply(dataLayer, arguments); }
//
//    Because `var` at script-tag scope leaks to `window`, `originalPush`
//    becomes `window.originalPush`. The tag fires on every history change,
//    so the SECOND firing captures the FIRST wrapper as originalPush.
//    Both wrappers reference the same `window.originalPush`, creating a
//    self-referential loop that infinite-recurses on every subsequent push.
//
// 3. Multiple gtag measurement snippets (GA4 properties, Ads conversions)
//    each install their own wrapper around `dataLayer.push`. On correctly-
//    scoped domains the chain terminates on the native array push, but
//    when GTM's environment / consent gating is misconfigured (e.g. site
//    running on an unexpected host) the wrappers can end up cross-calling
//    each other in a cycle:  A -> B -> C -> A -> B -> C -> ...  which
//    blows the stack with `RangeError: Maximum call stack size exceeded`
//    thrown from `gtag/js` and bubbles into React's scheduler.
//
// Mitigation layers (each is independent — any one alone stops its bug):
//   - Sanitize every payload we push (bounded depth, no DOM/functions/
//     circular refs, string/array length caps).
//   - Guard `window.originalPush` with a setter so the moment the delayed-
//     event-script tag tries to store the buggy wrapper there, we swap in
//     the native array push.
//   - Guard `dataLayer.push` with an accessor property whose setter
//     refuses to install the delayed-event-script wrapper (the only
//     known cycle-maker) into the chain, and whose base wrapper uses a
//     chain-depth counter that bails to `Array.prototype.push` once the
//     layered wrapper chain gets absurd (a real chain is ~5 deep;
//     cycles are unbounded).
//   - Wrap our own call site in try/catch as a final safety net.
// ─────────────────────────────────────────────────────────────────────────

const MAX_PAYLOAD_DEPTH = 6
const MAX_STRING_LENGTH = 5000
const MAX_ARRAY_LENGTH = 100
const MAX_OBJECT_KEYS = 200

const DELAYED_EVENT_SCRIPT_MARKER = "initAfterConsent"

const sanitizeValue = (
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown => {
  if (value === null || value === undefined) return value

  const type = typeof value
  if (type === "string") {
    const str = value as string
    return str.length > MAX_STRING_LENGTH ? str.slice(0, MAX_STRING_LENGTH) : str
  }
  if (type === "number" || type === "boolean") return value
  if (type === "bigint") return (value as bigint).toString()
  if (type === "function" || type === "symbol") return undefined

  if (depth >= MAX_PAYLOAD_DEPTH) return "[Truncated]"

  if (typeof window !== "undefined" && value === window) return "[Window]"
  if (typeof Element !== "undefined" && value instanceof Element) {
    return `[Element:${(value as Element).tagName?.toLowerCase() || "unknown"}]`
  }
  if (typeof Node !== "undefined" && value instanceof Node) return "[Node]"
  if (typeof Event !== "undefined" && value instanceof Event) {
    return `[Event:${(value as Event).type || "unknown"}]`
  }
  if (value instanceof Date) return value.toISOString()
  if (value instanceof Error) {
    return { name: value.name, message: value.message }
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return "[Circular]"
    seen.add(value)
    const out: unknown[] = []
    const len = Math.min(value.length, MAX_ARRAY_LENGTH)
    for (let i = 0; i < len; i++) {
      const sanitized = sanitizeValue(value[i], seen, depth + 1)
      out.push(sanitized === undefined ? null : sanitized)
    }
    return out
  }

  if (type === "object") {
    const obj = value as Record<string, unknown>
    if (seen.has(obj)) return "[Circular]"
    seen.add(obj)
    const out: Record<string, unknown> = {}
    let count = 0
    for (const key of Object.keys(obj)) {
      if (count >= MAX_OBJECT_KEYS) break
      // React fiber / DOM internal props leak circular refs — skip them.
      if (key.startsWith("__reactFiber") || key.startsWith("__reactProps")) {
        continue
      }
      const sanitized = sanitizeValue(obj[key], seen, depth + 1)
      if (sanitized !== undefined) {
        out[key] = sanitized
        count++
      }
    }
    return out
  }

  return undefined
}

const sanitizePayload = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  try {
    const sanitized = sanitizeValue(payload, new WeakSet<object>(), 0)
    if (sanitized && typeof sanitized === "object" && !Array.isArray(sanitized)) {
      return sanitized as Record<string, unknown>
    }
  } catch {
    // fall through
  }
  const eventName =
    typeof payload?.event === "string" ? (payload.event as string) : "unknown"
  return { event: eventName }
}

// Detect the delayed-event-script wrapper by its source signature. The
// tag's inline JS uses `initAfterConsent` as a nested var name and it
// survives minification because it's declared verbatim in the tag's
// custom HTML.
const looksLikeDelayedEventScriptWrapper = (fn: unknown): boolean => {
  if (typeof fn !== "function") return false
  try {
    return Function.prototype.toString
      .call(fn)
      .includes(DELAYED_EVENT_SCRIPT_MARKER)
  } catch {
    return false
  }
}

// Install a one-time guard on `window.originalPush` that silently swaps
// in the native array push whenever the delayed-event-script tag tries
// to store its own wrapper there.
//
// The tag's wrapper body is (paraphrased):
//   function() { ...; return originalPush.apply(dataLayer, arguments) }
// Because the tag ran at script-tag top-level scope, that `originalPush`
// binding is `window.originalPush`, resolved at call time. When the tag
// fires more than once (which GTM does on every history change), the
// SECOND firing does `window.originalPush = dataLayer.push` — capturing
// the FIRST wrapper — then installs a new wrapper. Every subsequent push
// through EITHER wrapper looks up the same `window.originalPush`, which
// now points at a wrapper, and infinite-recurses until the stack blows
// and the event is dropped. This also breaks `gtm.historyChange` /
// `gtm.historyChange-v2` events GTM emits during client-side navigation,
// which is what surfaces as the "This page couldn't load" screen.
//
// By intercepting writes to `window.originalPush` we prevent the buggy
// wrapper from ever being on the recursion path, without wrapping
// `dataLayer.push` ourselves (which would just get us captured by the
// next firing of the tag).
//
// Native Array.prototype.push is safe as the terminating target: GTM's
// own history/pageview tracking runs off its internal subscriber list
// and does not require our custom events to flow through its push
// wrapper to fire — losing that path is strictly better than dropping
// every event to a stack overflow.
const installOriginalPushGuard = () => {
  if (typeof window === "undefined") return
  const anyWindow = window as unknown as {
    __originalPushGuardInstalled?: boolean
    originalPush?: unknown
  }
  if (anyWindow.__originalPushGuardInstalled) return

  let stored: unknown = anyWindow.originalPush
  if (looksLikeDelayedEventScriptWrapper(stored)) {
    stored = Array.prototype.push
  }

  try {
    Object.defineProperty(window, "originalPush", {
      configurable: true,
      enumerable: true,
      get() {
        return stored
      },
      set(value: unknown) {
        stored = looksLikeDelayedEventScriptWrapper(value)
          ? Array.prototype.push
          : value
      },
    })
    anyWindow.__originalPushGuardInstalled = true
  } catch {
    // If defineProperty is blocked, fall back to a one-shot repair.
    if (looksLikeDelayedEventScriptWrapper(anyWindow.originalPush)) {
      anyWindow.originalPush = Array.prototype.push
    }
  }
}

// Depth at which we consider the wrapper chain a runaway cycle and bail
// to native `Array.prototype.push`. A healthy chain is 2–5 wrappers deep
// (delayed-event script, GTM raw push, one or two gtag wrappers). 16 is
// generous enough that no realistic legitimate chain hits it, but small
// enough to cut off an infinite cycle long before the JS engine's stack
// limit (typically 10k+ frames).
const MAX_PUSH_CHAIN_DEPTH = 16

// Sit in the `dataLayer.push` wrapper chain as a plain wrapper. If any
// downstream wrapper (GA4 gtag, Ads gtag, 6sense, etc.) develops a
// runaway cycle, our depth counter short-circuits to
// `Array.prototype.push` before the JS engine's stack blows, and our
// try/catch swallows a `RangeError` so it doesn't bubble into React's
// scheduler.
//
// How it works:
//   - At install we capture whatever `dataLayer.push` currently is
//     (`downstream`). On a fresh page this is `Array.prototype.push`;
//     if GTM already installed its own wrapper before we ran, that's
//     what we sit on top of, so GTM's tag processing still fires.
//   - We install an accessor on the array's `push` whose getter returns
//     the CURRENT top of the chain (`top`, initially `guardedPush`) and
//     whose setter replaces `top` — filtering out the delayed-event-
//     script wrapper (see below). Later wrappers (gtag snippets, etc.)
//     do the standard `oldPush = dl.push; dl.push = newWrapper` dance:
//     their `oldPush` captures the real previous top, so the chain
//     stays linear and acyclic.
//   - The getter must NOT always hand out `guardedPush` (i.e. force
//     ourselves to stay on top). That pattern is self-referential:
//     any downstream wrapper's captured `oldPush` would be
//     `guardedPush`, and its `oldPush.apply` re-enters `guardedPush`,
//     which then calls `underlying` = the same downstream wrapper —
//     infinite loop, only broken by the depth bail (which meant every
//     push was amplified 16x through the top-most wrapper, e.g.
//     re-firing the 6sense tag on every event).
//   - `guardedPush` increments a chain-depth counter, delegates to
//     `downstream`, and decrements in `finally`. If depth exceeds
//     `MAX_PUSH_CHAIN_DEPTH` we bypass the wrappers entirely and just
//     do `Array.prototype.push`, so the event lands and the stack
//     never overflows.
//   - If `downstream.apply` throws (e.g. a downstream wrapper still
//     hits `RangeError`), we catch and fall through to
//     `Array.prototype.push` so the event still lands and the throw
//     doesn't reach the caller.
const installDataLayerPushCycleGuard = () => {
  if (typeof window === "undefined") return
  const anyWindow = window as unknown as {
    __dataLayerPushGuardInstalled?: boolean
    dataLayer?: unknown[]
  }
  if (anyWindow.__dataLayerPushGuardInstalled) return

  const dl = (anyWindow.dataLayer = anyWindow.dataLayer || []) as unknown[]
  if (!Array.isArray(dl)) return

  const downstream: (this: unknown[], ...items: unknown[]) => number = (
    dl as { push: (this: unknown[], ...items: unknown[]) => number }
  ).push

  let depth = 0

  const guardedPush = function (
    this: unknown[],
    ...items: unknown[]
  ): number {
    const target = this || dl
    if (depth >= MAX_PUSH_CHAIN_DEPTH) {
      // Runaway chain — skip the wrappers, just land the events.
      try {
        return Array.prototype.push.apply(target, items)
      } catch {
        return target.length
      }
    }
    depth++
    try {
      return downstream.apply(target, items)
    } catch {
      // A downstream wrapper blew up (RangeError from a gtag cycle, a
      // serialization crash, etc.). Land the events in the array
      // ourselves so the caller — often React's scheduler — sees a
      // normal return rather than a thrown exception.
      try {
        return Array.prototype.push.apply(target, items)
      } catch {
        return target.length
      }
    } finally {
      depth--
    }
  }

  // Current top of the wrapper chain. Reads of `dl.push` return this;
  // assignments replace it — EXCEPT assignments of the delayed-event-script
  // wrapper, which we refuse to install. That wrapper is the only known
  // cycle-maker: it resolves `window.originalPush` at call time, and on its
  // SECOND install (GTM re-fires the tag on every history change, e.g. a
  // language switch) `originalPush` has been overwritten with a gtag wrapper
  // that chains back down to the FIRST copy, closing the loop
  //   gtagA -> gtagB -> gtagC -> delayed#1 -> originalPush(=gtagA) -> ...
  // The originalPush guard above can't catch that case because the value
  // written to `originalPush` is a gtag wrapper, not the marker-matching
  // delayed wrapper. Keeping the delayed wrapper out of the chain entirely
  // makes the cycle impossible while GTM/gtag wrappers still capture the
  // true previous `push` (the getter returns `top`, not `guardedPush`, so
  // the chain stays linear — see the accessor warning in the comment above).
  let top: (this: unknown[], ...items: unknown[]) => number = guardedPush

  try {
    Object.defineProperty(dl, "push", {
      configurable: true,
      enumerable: false,
      get() {
        return top
      },
      set(value: unknown) {
        if (typeof value !== "function") return
        if (looksLikeDelayedEventScriptWrapper(value)) return
        top = value as (this: unknown[], ...items: unknown[]) => number
      },
    })
    anyWindow.__dataLayerPushGuardInstalled = true
  } catch {
    try {
      ;(
        dl as { push: (this: unknown[], ...items: unknown[]) => number }
      ).push = guardedPush
      anyWindow.__dataLayerPushGuardInstalled = true
    } catch {
      // give up; sanitizer + originalPush guard + outer try/catch remain.
    }
  }
}

if (typeof window !== "undefined") {
  installOriginalPushGuard()
  installDataLayerPushCycleGuard()
}

export const getEnv = (hostname: string): string => {
  let environment = "unknown"
  if (hostname) {
    if (hostname.includes("localhost")) {
      environment = "local"
    } else if (
      hostname.includes("dev") ||
      hostname.includes("test") ||
      hostname.includes("preview")
    ) {
      environment = "dev"
    } else if (hostname.includes("stg") || hostname.includes("stage")) {
      environment = "stg"
    } else {
      environment = "prod"
    }
  }
  return environment
}

export const containsValidTargetClass = (
  validClasses: string[],
  target: any,
) => {
  // for use in event handlers - returns true if event target has a matching class in the supplied validClasses array
  let validClick = false

  validClasses?.forEach((validClass: any) => {
    if (target && target?.contains(validClass) && validClick === false) {
      validClick = true
    }
  })
  return validClick
}

const parseStoredJson = (value: string | null) => {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export const getStoredUserInfo = () => {
  if (typeof window === "undefined") {
    return null
  }

  return (
    parseStoredJson(window.sessionStorage.getItem("user_info")) ||
    parseStoredJson(window.localStorage.getItem("user_info"))
  )
}

export const syncStoredUserInfo = (
  userInfo: Record<string, unknown> | null,
) => {
  if (typeof window === "undefined") {
    return
  }

  if (!userInfo) {
    window.sessionStorage.removeItem("user_info")
    window.localStorage.removeItem("user_info")
    return
  }

  const serializedUserInfo = JSON.stringify(userInfo)
  window.sessionStorage.setItem("user_info", serializedUserInfo)
  window.localStorage.setItem("user_info", serializedUserInfo)
}

export const pushDataLayerEvent = (
  payload: Record<string, unknown>,
  options?: {
    clearEcommerce?: boolean
  },
) => {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.dataLayer = window.dataLayer || []
    // Ensure both guards are in place before every push, in case module
    // init ran before `window.dataLayer` existed (module-load ordering
    // vs. GTM's inline snippet), or before the delayed-event-script tag
    // was ready to be guarded.
    installOriginalPushGuard()
    installDataLayerPushCycleGuard()

    if (options?.clearEcommerce) {
      window.dataLayer.push({ ecommerce: null })
    }

    window.dataLayer.push(sanitizePayload(payload))
  } catch (err) {
    console.error("pushDataLayerEvent error", err)
  }
}

export const emitGTMEvent = (payload: Record<string, unknown>) => {
  pushDataLayerEvent(payload)
}

const normalizeFieldKey = (key: string) =>
  key
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

export const prefixFormFields = (formValues: Record<string, unknown> = {}) => {
  return Object.entries(formValues).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      const normalizedKey = normalizeFieldKey(key)
      if (!normalizedKey) return acc

      acc[`form_${normalizedKey}`] = value
      return acc
    },
    {},
  )
}

export const pushFormSubmitSuccess = ({
  formName,
  formType,
  formProvider,
  formId,
  formData,
  utmParams,
  calendlyId,
  calendlyUrl,
}: {
  formName: string
  formType: string
  formProvider: string
  formId?: string | null
  formData?: Record<string, unknown>
  utmParams?: Record<string, string | null>
  calendlyId?: string | null
  calendlyUrl?: string | null
}) => {
  pushDataLayerEvent({
    event: "form_submit_success",
    form_name: formName,
    form_type: formType,
    form_provider: formProvider,
    form_id: formId || null,
    calendly_id: calendlyId || null,
    calendly_url: calendlyUrl || null,
    utm_params: utmParams || null,
    form_data: formData || {},
    ...prefixFormFields(formData),
  })
}
